import type { Course } from "@repo/app-types/entities/course";
import {
  collectPlaybookSections,
  type PlaybookCriterionNode,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { ProjectWithJobType } from "@repo/app-types/entities/project";
import type { TrainingChapterAssessment } from "@repo/app-types/entities/trainingChapterAssessment";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { pillStyle } from "@repo/ui/Pills/pill";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Size } from "@repo/ui/helpers/helpers";
import { PopoverArrow } from "@repo/ui/popover/arrow";
import { popoverOffsetForArrow, popoverStyle } from "@repo/ui/popover/style";
import { type FC, useMemo } from "react";
import { Button, DialogTrigger, Heading, Popover } from "react-aria-components";
import { useAllTrainingChapterAssessmentsForProjectQuery } from "../../api/entities/trainingChapterAssessment.js";
import {
  AssessmentCriteriaNode,
  AssessmentCriteriaNodeCommentary,
  AssessmentCriteriaNodeGrading,
  AssessmentCriteriaNodeTitle,
} from "../../components/assessment/assessmentCriteriaNode.js";
import {
  AssessmentSectionNode,
  AssessmentSectionNodeContent,
  AssessmentSectionNodeTitle,
} from "../../components/assessment/assessmentSectionNode.js";
import { AssessmentTable } from "../../components/assessment/assessmentTable.js";
import { ImprovementRate } from "../insights/improvementRate.js";
import { CriterionAverageScore } from "../teammatePerformance/criterionAverageScore.js";
import { CriterionAverageScoreDialog } from "./criterionAverageScoreDialog.js";
import { CriterionImprovementRateDialog } from "./criterionImprovementRateDialog.js";
import {
  computeCriterionInsights,
  renderAssessableParticipantCount,
} from "./trainingInsightsHelpers.js";

export interface TrainingInsightsTabProps {
  project: ProjectWithJobType;
}

export const TrainingInsightsTab: FC<TrainingInsightsTabProps> = (props) => {
  const { data, isLoading } = useAllTrainingChapterAssessmentsForProjectQuery({
    projectId: props.project.id,
  });

  const renderCriterion = (
    course: Course,
    assessments: readonly TrainingChapterAssessment[],
    criterion: PlaybookCriterionNode,
  ) => {
    const insights = computeCriterionInsights(course, assessments, criterion);

    return {
      grading: (
        <div className="flex gap-1">
          <DialogTrigger>
            <CriterionAverageScore
              valueType={criterion.grading_value_type}
              value={insights?.lastChapter.value}
              onClick={() => {}}
            />

            {insights ? (
              <Popover
                className={popoverStyle().popover()}
                offset={popoverOffsetForArrow + 4}
              >
                <PopoverArrow />
                <CriterionAverageScoreDialog
                  criterion={criterion}
                  insights={insights}
                />
              </Popover>
            ) : null}
          </DialogTrigger>

          {insights?.improvementRate !== undefined ? (
            <DialogTrigger>
              <Button
                className={pillStyle({
                  isClickable: true,
                  size: Size.SMALL,
                  className:
                    "ring-transparent hover:bg-gray-500/10 pressed:bg-gray-500/20",
                })}
                onPress={() => {}}
              >
                <ImprovementRate rate={insights.improvementRate} />
              </Button>

              <Popover
                className={popoverStyle().popover()}
                offset={popoverOffsetForArrow + 4}
              >
                <PopoverArrow />
                <CriterionImprovementRateDialog
                  criterion={criterion}
                  insights={insights}
                />
              </Popover>
            </DialogTrigger>
          ) : null}
        </div>
      ),
      commentary: insights?.lastChapter.commentary,
    };
  };

  const sectionNodeIds = useMemo(
    () =>
      data?.assessmentTree
        ? collectPlaybookSections(data.assessmentTree).map(
            (section) => section.id,
          )
        : [],
    [data?.assessmentTree],
  );

  const renderNode = (
    course: Course,
    assessments: readonly TrainingChapterAssessment[],
    node: PlaybookNode,
  ) => {
    switch (node.type) {
      case "section": {
        return (
          <AssessmentSectionNode
            key={node.id}
            id={node.id}
            textValue={node.name}
          >
            <AssessmentSectionNodeContent>
              <AssessmentSectionNodeTitle>
                {node.name}
              </AssessmentSectionNodeTitle>
            </AssessmentSectionNodeContent>
            {renderNodes(course, assessments, node.nodes)}
          </AssessmentSectionNode>
        );
      }

      case "criteria": {
        const { grading, commentary } = renderCriterion(
          course,
          assessments,
          node,
        );

        return (
          <AssessmentCriteriaNode
            key={node.id}
            textValue={node.name}
            onPress={undefined}
          >
            <AssessmentCriteriaNodeTitle className="whitespace-normal">
              {node.name}
            </AssessmentCriteriaNodeTitle>
            <AssessmentCriteriaNodeGrading>
              {grading}
            </AssessmentCriteriaNodeGrading>
            <AssessmentCriteriaNodeCommentary>
              {commentary}
            </AssessmentCriteriaNodeCommentary>
          </AssessmentCriteriaNode>
        );
      }

      default: {
        assertUnreachable(node);
      }
    }
  };

  const renderNodes = (
    course: Course,
    assessments: readonly TrainingChapterAssessment[],
    nodes: readonly PlaybookNode[],
  ) => {
    return nodes.map((node) => renderNode(course, assessments, node));
  };

  return (
    <>
      <div className="mx-auto max-w-4xl">
        <div className="-mx-4 space-y-4 gap-y-6 px-4 py-8 sm:-mx-6 sm:space-y-6 sm:px-6">
          <section className="space-y-4">
            <Heading className="flex items-baseline gap-2">
              <span className="text-base font-semibold text-foreground">
                Playbook preparedness
              </span>
              {data ? (
                <span className="text-xs text-muted-foreground">
                  {renderAssessableParticipantCount(
                    data.assessableParticipantsCount,
                  )}
                </span>
              ) : null}
            </Heading>

            {!data && isLoading ? <DefaultSkeleton /> : null}

            {data?.assessmentTree === null ? (
              <div className="text-sm">No assessment template found</div>
            ) : null}

            {data?.assessmentTree ? (
              <AssessmentTable
                className="grid-cols-[minmax(0,3fr)_auto_minmax(0,6fr)] gap-x-4"
                defaultExpandedIds={sectionNodeIds}
              >
                {renderNodes(
                  data.course,
                  data.trainingChapterAssessments,
                  data.assessmentTree.nodes,
                )}
              </AssessmentTable>
            ) : null}
          </section>
        </div>
      </div>
    </>
  );
};
